import { ComboboxSelect, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@chronosphereio/chrono-ui';
import { PromQLEditor } from '@chronosphereio/editors/prometheus';
import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { useState } from 'react';
import {
  Notice,
  NoticeKind,
  PrometheusLabelNamesVariableOptions,
  PrometheusLabelValuesVariableOptions,
  PrometheusPromQLVariableOptions,
  parseTemplateVariableNames,
  replaceTemplateVariables,
  VariableOption,
} from '@/core';
import { GlobalFilter, OptionsEditorProps, QueryPluginContext, VariablePlugin } from '@/plugin-system';
import { useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';
import {
  getPrometheusClient,
  getPrometheusTimeRange,
  getRangeStep,
  MatrixData,
  PrometheusClient,
  ScalarData,
  usePrometheusClient,
  VectorData,
} from '../model';
import { appMetadataToHTTPHeaders } from '../utils';
import { injectGlobalFilters as injectGlobalFiltersIntoPromQLQuery } from './lib/promql-variables';
import { MatcherEditor } from './MatcherEditor';
import { getFuzzyMatchingMetrics } from './prometheus-time-series-query/promql-builder/metric-selection/metric-selection-utils';

// Cap the number of label-name options we feed into the fuzzy filter to keep the
// editor responsive in large tenants, users can still type a free-text label name.
const MAX_LABEL_NAME_OPTIONS = 500000;

interface PrometheusLabelSelectProps {
  label: string;
  value: string;
  onSelect: (value: string) => void;
  /**
   * Optional matchers to scope the `/api/v1/labels` lookup.
   */
  matchers?: string[];
}

/**
 * Combobox for picking a Prometheus label name in the variable editor.
 * Fetch is gated behind a click on the combobox so we never hit `/api/v1/labels` on mount.
 */
function PrometheusLabelSelect({ label, value, onSelect, matchers }: PrometheusLabelSelectProps) {
  const client = usePrometheusClient();
  const { absoluteTimeRange } = useTimeRange();
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);

  const timeRange = getPrometheusTimeRange(absoluteTimeRange);
  const datasourceUrl = client.options.datasourceUrl;
  const scopedMatchers = matchers?.length ? matchers : undefined;

  const queryKey = ['promLabelsScoped', datasourceUrl, timeRange.start, timeRange.end, scopedMatchers];

  const results = useQuery(
    queryKey,
    async () => {
      const r = await client.labelNames({
        'match[]': scopedMatchers,
        start: timeRange.start,
        end: timeRange.end,
      });
      return r?.data ?? [];
    },
    { enabled: hasOpenedOnce }
  );

  const hasError = Boolean(results.error);
  const errorMessage = hasError
    ? 'Failed to load labels in this time range. You can still type a label name.'
    : undefined;

  const handleFirstInteraction = () => {
    if (!hasOpenedOnce) {
      setHasOpenedOnce(true);
    }
  };

  return (
    // Gate the fetch behind the first interaction so we don't hit /api/v1/labels on mount.
    // TODO: expose onOpen callback on ComboboxSelect
    <div onPointerDown={handleFirstInteraction} onFocusCapture={handleFirstInteraction}>
      <ComboboxSelect
        label={label}
        value={value || null}
        onSelect={(v) => {
          onSelect(v ?? '');
        }}
        onReset={() => null}
        filterOptions={(options, inputValue) => {
          // Cap before fuzzy filtering so ufuzzy stays responsive on huge tenants.
          const capped = options.length > MAX_LABEL_NAME_OPTIONS ? options.slice(0, MAX_LABEL_NAME_OPTIONS) : options;
          return getFuzzyMatchingMetrics(capped, inputValue);
        }}
        searchDebounceMs={250}
        highlightSearchKeywords
        // Allow typing a custom label name so authors aren't blocked when the
        // metadata endpoint is slow or errors out in large tenants.
        allowCustomValue
        isLoading={results.isFetching}
        noOptionsText={errorMessage ?? (hasOpenedOnce ? 'No labels found' : 'Click to load labels')}
        options={results.data ?? []}
      />
    </div>
  );
}

function PrometheusLabelValuesVariableEditor(props: OptionsEditorProps<PrometheusLabelValuesVariableOptions>) {
  const { label_name, matchers } = props.value;
  return (
    <Stack mt={1} spacing={2}>
      <PrometheusLabelSelect
        label="Label name"
        value={label_name}
        matchers={matchers}
        onSelect={(value) => {
          props.onChange({ ...props.value, label_name: value });
        }}
      />
      <MatcherEditor
        matchers={props.value.matchers ?? []}
        onChange={(e) => {
          props.onChange({ ...props.value, matchers: e });
        }}
        isReadonly={props.isReadonly}
      />
    </Stack>
  );
}

function PrometheusLabelNamesVariableEditor(props: OptionsEditorProps<PrometheusLabelNamesVariableOptions>) {
  return (
    <Stack spacing={2}>
      <MatcherEditor
        matchers={props.value.matchers ?? []}
        isReadonly={props.isReadonly}
        onChange={(e) => {
          props.onChange({ ...props.value, matchers: e });
        }}
      />
    </Stack>
  );
}

function PrometheusPromQLVariableEditor(props: OptionsEditorProps<PrometheusPromQLVariableOptions>) {
  const { value } = props;
  const { label_name, query_type } = value;

  const client = usePrometheusClient();
  const promURL = client?.options.datasourceUrl;

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Typography variant="body1">Query type</Typography>
        <ToggleButtonGroup
          value={query_type ?? 'Instant'}
          onChange={(_, value) => props.onChange({ ...props.value, query_type: value })}
        >
          <ToggleButton value="Instant">Instant</ToggleButton>
          <ToggleButton value="Range">Range</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <PromQLEditor
        queryApiEndpoint={promURL}
        value={value.expr}
        onChange={(query) => {
          props.onChange({ ...props.value, expr: query });
        }}
        readOnly={props.isReadonly}
        width="100%"
      />
      <PrometheusLabelSelect
        label="Label name"
        value={label_name}
        onSelect={(value) => {
          props.onChange({ ...props.value, label_name: value });
        }}
      />
    </Stack>
  );
}

function capturingMatrix(matrix: MatrixData, label_name: string): string[] {
  const captured = new Set<string>();
  for (const sample of matrix.result) {
    const value = sample.metric[label_name];
    if (value !== undefined) {
      captured.add(value);
    }
  }
  return Array.from(captured.values());
}

function capturingVector(vector: VectorData, label_name: string): string[] {
  const captured = new Set<string>();
  for (const sample of vector.result) {
    const value = sample.metric[label_name];
    if (value !== undefined) {
      captured.add(value);
    }
  }
  return Array.from(captured.values());
}

/**
 * Takes a list of strings and returns a list of VariableOptions
 */
const stringArrayToVariableOptions = (values?: string[]): VariableOption[] => {
  if (!values) return [];
  return values.map((value) => ({
    value,
    label: value,
  }));
};

/**
 * Takes a list of matchers and a list of global filters and returns a list of matchers with the global filters injected
 * TODO: What should be done with the notices?
 */
const injectGlobalFiltersIntoMatchers = async (matchers: string[], globalFilters?: GlobalFilter[]) => {
  const results = await Promise.all(
    matchers.map(async (query) => {
      return await injectGlobalFiltersIntoPromQLQuery(query, globalFilters ?? []);
    })
  );
  return {
    matchers: results.map((r) => r.query),
    notices: results
      .map((r) => r.warnings)
      .flat()
      .map((warning) => warningStringToNotice(warning)),
  };
};

/**
 * Converts a warning string to a Notice
 */
function warningStringToNotice(warning: string): Notice {
  return {
    message: warning,
    type: 'warning',
    kind: NoticeKind.GlobalFilters,
  };
}

export const PrometheusLabelNamesVariable: VariablePlugin<PrometheusLabelNamesVariableOptions> = {
  getVariableOptions: async (spec, ctx) => {
    const client: PrometheusClient = getPrometheusClient(ctx.config);
    const match = spec.matchers;
    const timeRange = getPrometheusTimeRange(ctx.timeRange);

    const { data: options } = await client.labelNames({ 'match[]': match, ...timeRange }, getHeadersFromContext(ctx));
    return {
      data: stringArrayToVariableOptions(options),
    };
  },
  dependsOn: (spec) => {
    return {
      variables: spec.matchers?.map((m) => parseTemplateVariableNames(m)).flat() || [],
    };
  },
  OptionsEditorComponent: PrometheusLabelNamesVariableEditor,
  createInitialOptions: () => ({}),
  interpolateVariables: (spec, ctx) => {
    return {
      ...spec,
      matchers: spec.matchers?.map((m) => replaceTemplateVariables(m, ctx.variableState)),
    };
  },
  injectGlobalFilters: async (spec, ctx) => {
    // Create a new array for the matchers so we can add things to it if necessary
    const matchers = [...(spec.matchers ?? [])];
    // If there are no matchers, add an empty one so we can inject global filter values correctly
    if (matchers.length === 0 && ctx.globalFilters && ctx.globalFilters?.length > 0) {
      matchers.push('{}');
    }

    const resp = await injectGlobalFiltersIntoMatchers(matchers, ctx.globalFilters);
    return {
      spec: produce(spec, (draft) => {
        draft.matchers = resp.matchers;
      }),
      notices: resp.notices,
    };
  },
};

export const PrometheusLabelValuesVariable: VariablePlugin<PrometheusLabelValuesVariableOptions> = {
  getVariableOptions: async (spec, ctx) => {
    const pluginDef = spec;
    const client: PrometheusClient = getPrometheusClient(ctx.config);
    const match = pluginDef.matchers;

    const timeRange = getPrometheusTimeRange(ctx.timeRange);

    if (!pluginDef.label_name) {
      throw new Error('label_name is required');
    }

    const { data: options } = await client.labelValues(
      {
        labelName: pluginDef.label_name,
        'match[]': match,
        ...timeRange,
      },
      getHeadersFromContext(ctx)
    );
    return {
      data: stringArrayToVariableOptions(options),
    };
  },
  dependsOn: (spec) => {
    return {
      variables:
        spec.matchers
          ?.map((m) => parseTemplateVariableNames(m))
          .flat()
          .concat(parseTemplateVariableNames(spec.label_name)) || [],
    };
  },
  OptionsEditorComponent: PrometheusLabelValuesVariableEditor,
  createInitialOptions: () => ({ label_name: '' }),
  interpolateVariables: (spec, ctx) => {
    return {
      ...spec,
      matchers: spec.matchers?.map((m) => replaceTemplateVariables(m, ctx.variableState)),
      label_name: replaceTemplateVariables(spec.label_name, ctx.variableState),
    };
  },
  injectGlobalFilters: async (spec, ctx) => {
    // Create a new array for the matchers so we can add things to it if necessary
    const matchers = [...(spec.matchers ?? [])];
    // If there are no matchers, add an empty one so we can inject global filter values correctly
    if (matchers.length === 0 && ctx.globalFilters && ctx.globalFilters?.length > 0) {
      matchers.push('{}');
    }

    const resp = await injectGlobalFiltersIntoMatchers(matchers, ctx.globalFilters);
    return {
      spec: produce(spec, (draft) => {
        draft.matchers = resp.matchers;
      }),
      notices: resp.notices,
    };
  },
};

export const PrometheusPromQLVariable: VariablePlugin<PrometheusPromQLVariableOptions> = {
  getVariableOptions: async (spec, ctx) => {
    const client: PrometheusClient = getPrometheusClient(ctx.config);
    const timeRange = getPrometheusTimeRange(ctx.timeRange);

    const step = getRangeStep({
      timeRange,
      minStepSeconds: ctx.config?.prometheusScrapeIntervalSeconds,
      suggestedStepMs: ctx.suggestedStepMs,
    });
    let options: MatrixData | VectorData | ScalarData | undefined;

    const queryType = spec.query_type ?? 'Instant';
    if (queryType === 'Range') {
      const { data: rangeOptions } = await client.rangeQuery(
        {
          query: spec.expr,
          start: timeRange.start,
          end: timeRange.end,
          step: step,
        },
        getHeadersFromContext(ctx)
      );
      options = rangeOptions;
    } else {
      const { data: instantOptions } = await client.instantQuery(
        {
          query: spec.expr,
          time: timeRange.start,
        },
        getHeadersFromContext(ctx)
      );
      options = instantOptions;
    }

    const labelName = spec.label_name;
    let values: string[] = [];
    if (options?.resultType === 'matrix') {
      values = capturingMatrix(options, labelName);
    } else if (options?.resultType === 'vector') {
      values = capturingVector(options, labelName);
    }

    return {
      data: stringArrayToVariableOptions(values),
    };
  },
  dependsOn: (spec) => {
    return {
      variables: parseTemplateVariableNames(spec.expr).concat(parseTemplateVariableNames(spec.label_name)),
    };
  },
  OptionsEditorComponent: PrometheusPromQLVariableEditor,
  createInitialOptions: () => ({ expr: '', label_name: '' }),
  interpolateVariables: (spec, ctx) => {
    return {
      ...spec,
      expr: replaceTemplateVariables(spec.expr, ctx.variableState),
      label_name: replaceTemplateVariables(spec.label_name, ctx.variableState),
    };
  },
  injectGlobalFilters: async (spec, ctx) => {
    const result = await injectGlobalFiltersIntoPromQLQuery(spec.expr, ctx.globalFilters ?? []);
    return {
      spec: produce(spec, (draft) => {
        draft.expr = result.query;
      }),
      notices: result.warnings.map((warning) => warningStringToNotice(warning)),
    };
  },
};

function getHeadersFromContext(ctx: QueryPluginContext) {
  return ctx.appMetadata ? appMetadataToHTTPHeaders(ctx.appMetadata) : {};
}
